import type { Card, CardFormat } from '@/features/dashboard/api/schemas'
import type { ValueOptions } from './format'
import { changeFraction, chartTotals, formatValue } from './format'

export type Figure = {
  value: number
  /** The previous period's value, which the figure rolls from. */
  previous: number | null
  fraction: number | null
  unit?: string | null | undefined
  format: CardFormat
  kind: Exclude<Card['kind'], 'empty'>
}

const lastTwo = (data: (number | null)[]): [number | null, number | null] => {
  const values = data.filter((v): v is number => v !== null)
  return [values.at(-1) ?? null, values.at(-2) ?? null]
}

/** A card's headline: its value, or a chart's latest total. */
export const toFigure = (card: Card): Figure | null => {
  if (card.kind === 'empty') return null
  const { unit, format, kind } = card
  switch (card.kind) {
    case 'kpi':
      return {
        value: card.value,
        previous: card.previousValue ?? null,
        fraction: changeFraction(card.value, card.previousValue),
        unit,
        format,
        kind,
      }
    case 'chart': {
      // Older payloads have no headline: use the last totals, then series[0].
      const [last, previous] =
        card.value !== undefined && card.value !== null
          ? [card.value, card.previousValue ?? null]
          : lastTwo(chartTotals(card))
      return last === null
        ? null
        : {
            value: last,
            previous,
            fraction: changeFraction(last, previous),
            unit,
            format,
            kind,
          }
    }
  }
}

export const valueOptions = ({ unit, format, kind }: Figure): ValueOptions => ({
  unit,
  format,
  kind,
})

/** Another value of the figure's card (e.g. the previous one), formatted like it. */
export const formatLike = (figure: Figure, value: number) =>
  formatValue(value, valueOptions(figure))
